import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Debounce, useDragHandler } from '@medifind/hooks';
import { IconRightCaret } from '@medifind/icons';
import { MFLink } from '@medifind/shared-basic-components';
import { colorLookup, scrollToId, useEventListener } from '@medifind/utils';
import styles from './DoctorProfileNavMenu.module.scss';

const navMenuScrollDebounce = new Debounce(200).debounce;

const NavMenuLink = ({ id, targetId, name, scrolled, suppressMenuSlideOnScrollEvent }) => {
  const linkRef = useDragHandler({
    onMouseUp: (e, _, startPos) => {
      // this check prevents triggering click on "touch-slide" of nav-menu
      if (Math.abs(startPos.x - e.clientX) < 5) {
        const scrollendEventSupported = 'onscrollend' in window;
        if (scrollendEventSupported) {
          // this prevents left-right blinking of menu because scroll event fires several times
          suppressMenuSlideOnScrollEvent.current = true;
        }
        scrollToId(targetId, 0, 40);
      }
    },
  });

  useEffect(() => {
    const element = linkRef.current;
    if (!element) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        scrollToId(targetId, 0, 40);
      }
    };

    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [linkRef]);

  return (
    <MFLink
      id={id}
      ref={linkRef}
      noTabIndex={false}
      className={classNames(styles['nav-menu__link'], { [styles['highlighted']]: scrolled })}
    >
      {name}
    </MFLink>
  );
};

const DoctorProfileNavMenu = ({ showInsuranceSection, showAOESection = false }) => {
  const [scrolledIndex, setScrolledIndex] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const suppressMenuSlideOnScrollEvent = useRef(false);

  const sections = useMemo(
    () =>
      [
        { id: 'overview', name: 'Overview' },
        showInsuranceSection && { id: 'insurance', name: 'Insurance' },
        { id: 'location', name: 'Locations' },
        showAOESection ? { id: 'areas-of-expertise', name: 'Areas of Expertise' } : null,
        { id: 'research', name: 'Clinical Research' },
      ].filter(Boolean),
    [showAOESection, showInsuranceSection],
  );

  const getScrolledIndex = () => {
    let scrolledIndex = 0;
    sections.forEach((section, index) => {
      const sectionElem = document.getElementById(section.id);
      if (!sectionElem) return;
      const sectionRect = sectionElem.getBoundingClientRect();
      if (sectionRect.top <= 60) scrolledIndex = index;
      else return;
    });
    return scrolledIndex;
  };

  const menuRef = useDragHandler({
    // enables "touch-slide" of nav-menu for non touch devices
    onMouseMove: (e) => {
      menuRef.current?.scrollBy({
        left: -e.movementX,
        behavior: 'instant',
      });
    },
  });

  const slideToNavItem = (scrolledIndex) => {
    const navItem = document.getElementById('navlink-' + sections[scrolledIndex].id);
    if (!navItem) return;
    menuRef.current?.scroll({
      left: menuRef.current.offsetLeft + navItem.offsetLeft,
      behavior: 'smooth',
    });
  };

  const slideNavMenuRight = () => {
    if (menuRef.current.scrollLeft + menuRef.current.offsetWidth >= menuRef.current.scrollWidth) {
      // loop sliding
      menuRef.current.scroll({
        left: 0,
        behavior: 'smooth',
      });
    } else {
      menuRef.current.scrollBy({
        left: 200,
        behavior: 'smooth',
      });
    }
  };

  useEventListener('scroll', () => {
    const scrolledIndex = getScrolledIndex();
    setScrolledIndex((prev) => {
      if (prev != scrolledIndex && !suppressMenuSlideOnScrollEvent.current) {
        navMenuScrollDebounce(() => slideToNavItem(scrolledIndex));
      }
      return scrolledIndex;
    });
  });

  useEventListener('scrollend', () => {
    slideToNavItem(getScrolledIndex());
    suppressMenuSlideOnScrollEvent.current = false;
  });

  useEffect(() => {
    const onResize = () => {
      setIsOverflow(menuRef.current?.offsetWidth < menuRef.current?.scrollWidth);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [menuRef]);

  return (
    <div className={styles['nav-menu']}>
      <div ref={menuRef} className={styles['nav-menu__links-container']}>
        {sections.map((item, index) => (
          <NavMenuLink
            key={item.id}
            id={'navlink-' + item.id}
            name={item.name}
            targetId={item.id}
            scrolled={index == scrolledIndex}
            suppressMenuSlideOnScrollEvent={suppressMenuSlideOnScrollEvent}
          />
        ))}
      </div>
      {isOverflow && (
        <MFLink className={classNames(styles['nav-menu__slide-right'])} onClick={slideNavMenuRight} aria_label={`nav_menu_scroll`}>
          <IconRightCaret color={colorLookup('blue')} />
        </MFLink>
      )}
    </div>
  );
};

export { DoctorProfileNavMenu as default, DoctorProfileNavMenu };
